/**
 * DevOrganizationSwitcher Component
 *
 * A testing tool that allows developers to switch between organizations
 * during development to test multi-tenant functionality without setting up
 * subdomain routing.
 *
 * Features:
 * - Fetches all organizations from the main database
 * - Allows switching organization context
 * - Forces logout and re-login for new organization context
 * - Only shows in development environment
 * - Floating interface that doesn't interfere with main UI
 * - Role emulation for testing permissions
 * - Time bank and authentication testing tools
 */

import React, { useState, useCallback } from 'react';
import {
    FiSettings,
    FiDatabase,
    FiX,
    FiInfo,
    FiChevronDown
} from 'react-icons/fi';

import useDevOrganizationSwitcher from './hooks/useDevOrganizationSwitcher';
import OrganizationList from './components/OrganizationList';
import SettingsTab from './components/SettingsTab';
import TimeBankTab from './components/TimeBankTab';
import AuthTab from './components/AuthTab';
import RolesTab from './components/RolesTab';
import ImpersonateTab from './components/ImpersonateTab';
import CurrentContext from './components/CurrentContext';
import InfoPanel from './components/InfoPanel';
import TabNavigation from './components/TabNavigation';
import WelcomeBack from '../WelcomeBack';

import '../../styles/DevOrganizationSwitcher.css';

const DevOrganizationSwitcher = () => {
    // Welcome Back state
    const [showWelcomeBack, setShowWelcomeBack] = useState(false);

    const {
        // State
        isOpen,
        setIsOpen,
        organizations,
        loading,
        currentOrgId,
        currentOrg,
        isMinimized,
        setIsMinimized,
        showInfo,
        setShowInfo,
        isVisible,
        bypass2FA,
        activeTab,
        setActiveTab,
        roles,
        currentRoleId,
        emulatingRole,

        // Functions
        fetchOrganizations,
        switchToOrganization,
        emulateRole,
        stopEmulatingRole,
        toggle2FABypass,
        triggerUpdateBanner,
        showNotification,

        // Context
        getApiUrl,
        getCookie,
        getUser,
        triggerSessionExpiredModal,
        apiRequest,
        refreshNotifications
    } = useDevOrganizationSwitcher();

    // Trigger welcome back animation
    const triggerWelcomeBack = useCallback(() => {
        setShowWelcomeBack(true);
    }, []);

    // Check if we're in development mode
    const DEV = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV === 'true';

    // Only show in development
    if (!DEV) {
        return null;
    }

    // Don't render anything if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <>
            {/* Floating Trigger Button */}
            <div
                className="dos-floating-trigger"
                onClick={() => setIsOpen(!isOpen)}
            >
                <FiSettings className="dos-trigger-icon" />
                <div className="dos-dev-badge">DEV</div>
            </div>

            {/* Main Admin Panel Modal */}
            {isOpen && (
                <>
                    <div className="dos-overlay" onClick={() => setIsOpen(false)} />
                    <div className="dos-admin-panel">
                        {/* Header */}
                        <div className="dos-panel-header">
                            <div className="dos-header-left">
                                <FiDatabase className="dos-header-icon" />
                                <div className="dos-header-text">
                                    <h3 className="dos-panel-title">Development Admin Panel</h3>
                                    <p className="dos-panel-subtitle">Organization & Environment Management</p>
                                </div>
                            </div>
                            <div className="dos-header-actions">
                                <button
                                    className="dos-header-button dos-info-button"
                                    onClick={() => setShowInfo(!showInfo)}
                                    title="Show Info"
                                >
                                    <FiInfo />
                                </button>
                                <button
                                    className="dos-header-button dos-minimize-button"
                                    onClick={() => setIsMinimized(!isMinimized)}
                                    title="Toggle Size"
                                >
                                    <FiChevronDown className={isMinimized ? 'dos-rotated' : ''} />
                                </button>
                                <button
                                    className="dos-header-button dos-close-button"
                                    onClick={() => setIsOpen(false)}
                                    title="Close Panel"
                                >
                                    <FiX />
                                </button>
                            </div>
                        </div>

                        {/* Info Panel */}
                        {showInfo && (
                            <InfoPanel organizationsCount={organizations.length} />
                        )}

                        <div className="dos-panel-content">
                            {!isMinimized && (
                                <>
                                    {/* Tab Navigation */}
                                    <TabNavigation
                                        activeTab={activeTab}
                                        onTabChange={setActiveTab}
                                    />

                                    {/* Current Context */}
                                    <CurrentContext
                                        currentOrgId={currentOrgId}
                                        currentOrg={currentOrg}
                                    />

                                    {/* Impersonate Tab Content */}
                                    {activeTab === 'impersonate' && (
                                        <ImpersonateTab
                                            getApiUrl={getApiUrl}
                                            getCookie={getCookie}
                                            apiRequest={apiRequest}
                                            currentOrgId={currentOrgId}
                                            onShowNotification={showNotification}
                                        />
                                    )}

                                    {/* Settings Tab Content */}
                                    {activeTab === 'settings' && (
                                        <SettingsTab
                                            bypass2FA={bypass2FA}
                                            onToggle2FA={toggle2FABypass}
                                            onTriggerUpdateBanner={triggerUpdateBanner}
                                            onShowNotification={showNotification}
                                            onTriggerWelcomeBack={triggerWelcomeBack}
                                            apiRequest={apiRequest}
                                        />
                                    )}

                                    {/* Time Bank Tab Content */}
                                    {activeTab === 'timebank' && (
                                        <TimeBankTab
                                            apiRequest={apiRequest}
                                            onShowNotification={showNotification}
                                            onRefreshNotifications={refreshNotifications}
                                        />
                                    )}

                                    {/* Organizations Tab Content */}
                                    {activeTab === 'organizations' && (
                                        <OrganizationList
                                            organizations={organizations}
                                            currentOrgId={currentOrgId}
                                            loading={loading}
                                            onRefresh={fetchOrganizations}
                                            onSelectOrganization={switchToOrganization}
                                        />
                                    )}

                                    {/* Auth Tab Content */}
                                    {activeTab === 'auth' && (
                                        <AuthTab
                                            getApiUrl={getApiUrl}
                                            getCookie={getCookie}
                                            getUser={getUser}
                                            triggerSessionExpiredModal={triggerSessionExpiredModal}
                                            apiRequest={apiRequest}
                                            onShowNotification={showNotification}
                                        />
                                    )}

                                    {/* Roles Tab Content */}
                                    {activeTab === 'roles' && (
                                        <RolesTab
                                            roles={roles}
                                            currentRoleId={currentRoleId}
                                            emulatingRole={emulatingRole}
                                            getUser={getUser}
                                            onEmulateRole={emulateRole}
                                            onStopEmulating={stopEmulatingRole}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Welcome Back Animation */}
            <WelcomeBack
                isVisible={showWelcomeBack}
                onClose={() => setShowWelcomeBack(false)}
                userName={getUser()?.first_name || getUser()?.username || 'Developer'}
            />
        </>
    );
};

export default DevOrganizationSwitcher;
